import { LockOutlined } from "@ant-design/icons";
import {
  ABU,
  BtnGroup,
  buildSubView,
  CityName,
  Em,
  FormWrapper,
  Md,
  modalConfirm,
  NormalDist,
  playContext,
  showMsg,
  TestPage,
} from "@client";
import { Style } from "@client/style";
import { css } from "@emotion/css";
import { NpcModel } from "@npc";
import { calcScore, NumberUtil, SceneConst, SubNamespace } from "@share";
import { Alert, Button, DatePicker, Divider, Input, Modal, ModalFuncProps, Radio, Space, Table } from "antd";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";

const StartMoment = moment();
const { Column } = Table;
function Play({ playDone }: { playDone: () => void }) {
  const sceneContext = useContext(playContext);
  const {
    playerState: { ticketList },
    saveResult,
    params: { searchCost },
  } = sceneContext;
  const [showLocked, setShowLocked] = useState(false);
  const [lockCost, setLockCost] = useState(0);
  const [day, setDay] = useState(0);
  const [tradeIndex, setTradeIndex] = useState<number>(null!);
  const [lockList, setLockList] = useState(ticketList.map(() => false)),
    lock = (i: number) => {
      const _lockList = lockList.slice();
      _lockList[i] = true;
      setLockList(_lockList);
      setLockCost(lockCost + SceneConst.buyTicket.airlines[ticketList[i].airline].lockPrice);
    };
  const dayLeft = SceneConst.buyTicket.days - day;
  useEffect(() => {
    switch (dayLeft) {
      case SceneConst.buyTicket.days:
        break;
      case 1:
        showMsg(`老板将在明天出发前往【${CityName.to}】，今天务必订好机票哦！`, {
          okText: "好的，马上预订",
        });
        break;
      default:
        showMsg(`距离出发还剩【${dayLeft}】天，请尽快预订机票。`);
        break;
    }
  }, [dayLeft]);
  const onTrade = async (tradeIndex: number) => {
    setTradeIndex(tradeIndex);
    const { price, airline } = ticketList[tradeIndex],
      { company, time } = SceneConst.buyTicket.airlines[airline],
      totalSearchCost = day * searchCost;
    await showMsg(
      <p>
        花费<Em>{totalSearchCost}</Em>的搜寻成本和<Em>{lockCost}</Em>
        的锁定成本后， 你找到一张
        <Em>
          {company}：{time}
        </Em>
        的机票，价格为
        <Em>{price}</Em>，恭喜你。
      </p>,
      {
        okText: "知道了",
      }
    );
    const prices = ticketList.map((t) => t.price);
    saveResult({
      score: calcScore(NumberUtil.getMax(prices), prices[tradeIndex], NumberUtil.getMin(prices)),
      tradeIndex,
    });
    playDone();
  };
  if (tradeIndex != null) {
    return null;
  }
  const ticketTableData = ticketList.map((ticket, i) => {
      const airline = SceneConst.buyTicket.airlines[ticket.airline];
      return {
        i,
        locked: lockList[i],
        ...ticket,
        ...airline,
      };
    }),
    lockedTickets = ticketTableData.filter((_, i) => lockList[i]);
  const inputStyle = css`
    width: 8rem;
  `;
  const commonColumn = (
    <>
      <Column title="航班" dataIndex="company" />
      <Column title="时间" dataIndex="time" />
      <Column
        title="价格"
        dataIndex="price"
        render={(price) => <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>{price}</span>}
      />
    </>
  );
  return (
    <FormWrapper>
      <Alert
        showIcon
        type="warning"
        message={`距离出发还剩【${dayLeft}】天，请尽快预订机票（部分航班的机票支持锁定）！`}
      />
      <section
        style={{
          margin: "1rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div>
          <div>
            航班：
            <Input className={inputStyle} disabled value={CityName.from} />
            &nbsp;——&gt;&nbsp;
            <Input className={inputStyle} disabled value={CityName.to} />
          </div>
        </div>
        <Divider type="vertical" />
        <div>
          起飞日期：
          <DatePicker disabled value={StartMoment.add(SceneConst.buyTicket.days - 1, "day")} />
        </div>
      </section>
      <Table
        size="small"
        pagination={false}
        dataSource={ticketTableData.filter((s) => s.day === day)}
        className={css`
          max-width: 72rem;
          margin: auto;
        `}
      >
        {commonColumn}
        <Column title="锁定费用" dataIndex="lockPrice" render={(lockPrice) => lockPrice || "不支持"} />
        <Column
          title="状态"
          dataIndex="locked"
          render={(locked) => locked && <LockOutlined style={{ fontSize: "1.5rem" }} />}
        />
        <Column
          title="操作"
          width="20rem"
          render={({ lockPrice, locked, i }) => (
            <Space>
              <Button
                style={{
                  visibility: !lockPrice || locked ? "hidden" : "visible",
                }}
                onClick={() => lock(i)}
                className={Style.btnWarningSX}
              >
                锁定
              </Button>
              <Button className={Style.btnPrimaryXS} onClick={() => onTrade(i)}>
                购买
              </Button>
            </Space>
          )}
        />
      </Table>
      <BtnGroup>
        <Button
          disabled={!lockedTickets.length}
          onClick={() => setShowLocked(true)}
          className={lockedTickets.length ? Style.btnWarningSm : Style.btnDisabledSm}
        >
          已锁定<Em>{lockedTickets.length}</Em>张
        </Button>
        <Button
          className={Style.btnPrimarySm}
          disabled={dayLeft <= 1}
          onClick={() => {
            const confirmProps: ModalFuncProps = {
              content: `将在明天再次花费【${searchCost}】单位的时间成本搜寻机票价格？`,
              onOk: () => setDay(day + 1),
            };
            modalConfirm(confirmProps);
          }}
        >
          明天再订
        </Button>
      </BtnGroup>
      <Modal
        {...Md.fnProps}
        visible={showLocked}
        cancelButtonProps={{ hidden: true }}
        onOk={() => setShowLocked(false)}
      >
        <Table size="small" pagination={false} dataSource={lockedTickets}>
          <Column
            title="日期"
            dataIndex="day"
            render={(day) => StartMoment.add(day, "day").format(moment.HTML5_FMT.DATE)}
          />
          {commonColumn}
          <Column
            title="操作"
            render={({ i }) => (
              <Button className={Style.btnPrimaryXS} onClick={() => onTrade(i)}>
                购买
              </Button>
            )}
          />
        </Table>
        <p
          className={css`
            margin-top: 1rem;
            text-align: center;
          `}
        >
          已锁定<Em>{lockedTickets.length}</Em>张机票，锁定花费
          <Em>{lockCost}</Em>
        </p>
      </Modal>
    </FormWrapper>
  );
}

function Test({ done }: { done: (answers, answerScores) => void }) {
  return (
    <TestPage
      questions={[
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                若买家已为某一件产品支付了定金100元，并和卖家约定在未来以1000元购买某一产品。则该买家的【保留效用】，随着搜寻的行进，相比于没有支付定金的情形是上升了还是下降了？
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A. 上升</Radio>
                <Radio value={2}>B. 下降</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => (
            <p>
              搜寻时，买方知道，如果放弃之前的选项，会额外的损失定金，这相当于使搜寻到的选项变的更“贵”了，从该选项中获得的额外效用也下降了。得知未来的选项的效用没有那么高的情形下，买家的保留效用会下降。即其更愿意接受当下的选项。
            </p>
          ),
          answer: [2],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                假设买家在每一次搜寻的时候，都能看到两趟航班的实时价格；再假设每趟航班的价格都均匀的分布在600和1000之间。那么消费者预期每次搜寻的最低价格是多少？
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A. 600</Radio>
                <Radio value={2}>B. 733</Radio>
                <Radio value={3}>C. 850</Radio>
                <Radio value={4}>D. 950</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => <img src={`${ABU}answer/3.2.png`} />,
          answer: [2],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                若买家在购买机票时选择了锁定机票，并且支付了锁定的价格,相当于获得了一个在未来返回购买的机会。对一个卖家来说，因为每一个座位（比如普通经济舱座位）的成本相似，因此他的最优策略是：
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A.尽可能使用高的锁定价格</Radio>
                <Radio value={2}>B.使锁定价格覆盖买家的预期收益与当下收益的缺口</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => (
            <p>
              锁定价格使消费者在搜寻以后保有更好的备选项，同时也因为锁定费而为之付出成本，这种成本与简单搜寻中的搜寻成本类似。
            </p>
          ),
          answer: [1],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                主观题：请指出下列每条描述在多大程度上反映了您的情况，并在符合您的情况的选项序号上划勾（1-完全不符合；2-不符合；3一不确定；4一符合；5-非常符合）。
              </p>
              <br />
              <ul>
                {[
                  "我难以集中注意力。",
                  "我能为了一个长远目标高效地工作。",
                  "有时我会忍不住去做一些事情，即使我知道那样做是错误。",
                  "我常常考虑不周就付诸行动。",
                  "我希望我有更多的自律。",
                ].map((question) => (
                  <li style={{ listStyle: "decimal" }}>
                    <p>{question}</p>
                    <Radio.Group {...inputProps({ margin: ".5rem" })}>
                      {Array(5)
                        .fill(null)
                        .map((_, i) => (
                          <Radio value={i + 1}>{i + 1}</Radio>
                        ))}
                    </Radio.Group>
                  </li>
                ))}
              </ul>
            </div>
          ),
        },
        {
          isAnalysis: true,
          Content: ({ inputProps }) => (
            <div>
              <p>市场搜寻会受到哪些心理因素的影响？ 本题为主观题，无正确的答案，只需说明理由。</p>
              <br />
              <Input.TextArea maxLength={2e3} rows={4} {...inputProps({ width: "100%" })} />
            </div>
          ),
        },
      ]}
    />
  );
}

function BuyTicketIntro4th() {
  const [n, setN] = useState(0),
    [v, setV] = useState(0);
  return (
    <div>
      4.体验随机分布：你可通过点击以下【体验搜寻】按钮，体验机票价格【air_price】的（随机）变化。
      <table className={Style.tableInIntro}>
        <tbody>
          <tr>
            <td rowSpan={2}>
              第<Em>{n}</Em>次搜寻
            </td>
            <td>本次搜得的机票价格为</td>
          </tr>
          <tr>
            <td>
              <Em>{v}</Em>
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        机票价格服从均值为<Em>{SceneConst.buyTicket.price.MEAN}</Em>，标准差为
        <Em>{SceneConst.buyTicket.price.STD}</Em>的<NormalDist />。
      </p>
      <div style={{ margin: "1rem", textAlign: "right" }}>
        <Button
          className={Style.btnPrimarySm}
          onClick={() => {
            setN(n + 1);
            setV(NumberUtil.normalDist(SceneConst.buyTicket.price));
          }}
        >
          体验搜寻
        </Button>
      </div>
    </div>
  );
}

export const BuyTicketView = buildSubView({
  namespace: SubNamespace.BuyTicket,
  bg: `${ABU}scene/bg/BuyTicket.png`,
  label: "买机票",
  withSearch: true,
  npcModel: NpcModel.BuyTicket,
  introMsg: () => [
    ...`1.\t你的任务是为公司订数张去不同目的地的机票。你必须购买一张机票。
2.\t请在以下的三个航空公司【A】，【B】，和【C】两个不同时间点【上午】、【下午】中搜寻合适的机票价格。你在搜寻过程中产生成本较小，为【S3】，但是机票价格存在较大的不确定性。`
      .trim()
      .split("\n"),
    <>
      3.经过调查，你对机票价格形成了一定的预期。该航线的历史机票价格约在【
      {SceneConst.buyTicket.price.MEAN}】左右；同时它的标准差大约为【
      {SceneConst.buyTicket.price.STD}】，呈
      <NormalDist />。
    </>,
    <BuyTicketIntro4th />,
    <>
      5.在该任务中有的航空公司将允许你支付一个机票价格锁定费【S_lock】：如果你支付此费用，则可以把某一次搜寻到的价格
      <a
        onClick={() =>
          Modal.info({
            ...Md.fnProps,
            title: "票价锁定",
            content: (
              <p>
                <Em>锁定票价</Em>
                是部分航空公司（比如美联航、新加坡航空等）提供的一个预订选项。该选项让你有机会将自己的预订和票价保留一定的时间。这样，你就有更充足的时间来确认自己的旅行细节，而在此期间不用担心机票出现涨价的情况。
              </p>
            ),
          })
        }
      >
        &nbsp;锁定&nbsp;
      </a>
      。
    </>,
    `6.你可以通过点击【查看】获得该航司的机票报价【w】；同时你可以点击【锁定】，支付并锁定该价格。`,
    `7.\t你需要决定是否【购买】机票或者【锁定】机票价格。注意：机票的价格变化是实时的，如果不锁定价格，你只能选择现有的价格或者继续搜寻。`,
  ],
  npcWelcome: async () => {
    await showMsg(
      <>
        你的老板外派你进行一项工作。你需要购买一张<Em>{SceneConst.buyTicket.days}</Em>天后去<Em>{CityName.to}</Em>
        的机票。航空公司的机票价格具有很大的不确定性，可能根据航段的需求情况在不断的变化中。如何找到更便宜的机票呢，你打算试试在不同的时间段搜索机票。
      </>,
      {
        okText: "下一步",
      }
    );
    await showMsg("你的任务是在接下来的时间里，寻找并购买机票。", {
      okText: "知道了",
    });
  },
  Play,
  Test,
  initState: (s) => {
    s.ticketList = [];
    for (let day = 0; day < SceneConst.buyTicket.days; day++) {
      SceneConst.buyTicket.airlines.forEach((_, airline) => {
        s.ticketList.push({
          day,
          airline,
          price: NumberUtil.normalDist(SceneConst.buyTicket.price),
        });
      });
    }
  },
});
